import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Image } from 'react-bootstrap';
import moment from 'moment';

import ButtonLoader from './ButtonLoader';
import { loaderStatus } from '../store/features/loader';
import { getUserProfile } from '../store/features/auth';
import { NUMBERS } from '../resources/number-constants';
import { ROUTES } from '../resources/routes-constants';
import { EXTERNAL_URLS } from '../resources/url-constants';
import { getValue, renderCustomCloseButton, storeValue } from '../utility/helpers';
import { shouldShowUpgradeButton, handleUpgrade as handleUpgradeUtil } from '../utility/tierUtils';
import ThemeChange from '../components/common/ThemeChange';
import DropdownMenu from '../components/common/DropdownMenu';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Description from '../components/employer/JobDescription/Description';
import { approvedJob, generateCandidate, getJobDetail, resetCandidates, resetSearchFilters } from '../store/features/dashboard';
import {
  buildGenerateCandidatePayload,
  CachedCandidateFilters,
  clearActiveCandidateFilters,
} from '../utility/candidateFilterState';
import { FEATURE_FLAGS } from '../utility/featureFlags';
import { aiChat, chatBottonIcon, doneTick, likeWhite, logo } from '../assets/images/images';
import { toast } from 'react-toastify';
import { usePageTitle } from '../hooks/usePageTitle';

const getSemanticStorageKey = (id: string) => `job:${id}:semanticSearch`;

const readSemanticPreference = (id: string) => {
  try {
    return localStorage.getItem(getSemanticStorageKey(id)) === 'true';
  } catch {
    return false;
  }
};

const JobDescriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(loaderStatus);
  const queryParams = new URLSearchParams(location.search);
  const jobId = queryParams.get('jobId') || '9';
  const jobAction = queryParams.get('action') || '';
  const aboutFor = queryParams.get('about') || '';
  const endpoint = location.pathname.substring(1);
  const checkGenerate = getValue('isGenerate');

  const [loading, setLoading] = useState(false);
  const [jobData, setJobData] = useState<any>({});
  const [showLoader, setShowLoader] = useState(false);
  const [generatedDate, setGeneratedDate] = useState('');
  const [dataGenerated, setDataGenerated] = useState(true);
  // Try to get cached tier data for faster initial render
  const cachedTierData = getValue('tierData') ? JSON.parse(getValue('tierData') || '{}') : {};
  const [assignedTier, setAssignedTier] = useState<any>(cachedTierData);
  const [hideChatWindow, setHideChatWindow] = useState(true);
  const [idealizeJobData, setIdealizeJobData] = useState<any>([]);
  const [percentageFitValue, setPercentageFitValue] = useState<any>(null);
  const [havesData, setHavesData] = useState<any>([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [isSemanticEnabled, setIsSemanticEnabled] = useState(() =>
    FEATURE_FLAGS.enableSemanticSearch ? readSemanticPreference(jobId) : false,
  );
  const [cachedCandidateFilters, setCachedCandidateFilters] = useState<CachedCandidateFilters | null>(null);

  // Define routes and headings in a map to reduce repetitive conditionals
  const routesMap: Record<string, string> = {
    'job-description': '/idealize-candidate',
    'idealize-candidate': '/job-description-summary',
    'job-description-summary': '/candidate-selection',
    'candidate-selection': '/qualified-candidates',
  };

  // 'job-description': jobAction !== 'edit' ? 'Job Search Review' : 'Job description Prioritization'
  const headingMap: Record<string, string> = {
    'job-description': 'Job Description',
    'idealize-candidate': 'Idealized Candidate Prioritization',
    'job-description-summary': aboutFor === 'saved' ? 'Saved job' : aboutFor === 'duplicateSaved' ? 'duplicate job search' : 'Review Summary',
    'candidate-selection': 'Candidate Selection',
  };

  const approveRoute = routesMap[endpoint] || '/candidate-selection';
  const pageHeading = headingMap[endpoint] || 'Candidate Selection';

  usePageTitle(pageHeading);

  const nextStep = endpoint === 'job-description'
    ? NUMBERS.TWO
    : endpoint === 'idealize-candidate'
      ? NUMBERS.THREE
      : endpoint === 'job-description-summary'
        ? NUMBERS.FOUR
        : NUMBERS.FIVE;

  const navigateTo = (route: string) => navigate(route);

  const handleUpgrade = () => {
    handleUpgradeUtil(navigate, ROUTES.CONTACT_US_TO_UPGRADE_ROUTE);
  };

  // Render button text dynamically
  const getButtonText = () => {
    if (showLoader) {
      return <ButtonLoader />;
    }
    if (jobAction === 'edit') {
      return 'Done';
    }

    switch (endpoint) {
      case 'job-description':
        return 'Approve & Next';
      case 'idealize-candidate':
        return 'Approve';
      case 'job-description-summary':
        return aboutFor === 'saved' ? 'Generate Candidates' : 'Confirm Job';
      case 'candidate-selection':
        return 'See Candidates';
      default:
        return '';
    }
  };

  // Consolidate the approve logic
  const handleApprove = async () => {
    const buttonText = getButtonText();
    if (buttonText === 'Generate Candidates' || buttonText === 'Confirm Job') {
      if (endpoint === 'job-description'
        || endpoint === 'job-description-summary') {
        for (const list of idealizeJobData) {
          if (list.title === "Won't-Haves") continue;
          if (!list.items || list.items.length === 0) {
            toast.error(`'${list.title}' should have at least 1 input.`, { closeButton: renderCustomCloseButton() });
            return false;
          }
        }
      } else {
        for (const list of havesData) {
          if (list.title === "Won't-Haves") continue;
          if (!list.items || list.items.length === 0) {
            toast.error(`'${list.title}' should have at least 1 input.`, { closeButton: renderCustomCloseButton() });
            return false;
          }
        }
      }
    }

    if (jobAction === 'edit') {
      if (endpoint === 'idealize-candidate') {
        for (const list of havesData) {
          if (list.title === "Won't-Haves") continue;
          if (!list.items || list.items.length === 0) {
            toast.error(`'${list.title}' should have at least 1 input.`, { closeButton: renderCustomCloseButton() });
            return false;
          }
        }
      }
      if (aboutFor) {
        navigateTo(`/job-description-summary?jobId=${jobId}&about=${aboutFor}`);
      } else {
        navigateTo(`/job-description-summary?jobId=${jobId}`);
      }
    } else {
      if (endpoint === 'candidate-selection') {
        const payload = buildGenerateCandidatePayload(
          jobId,
          percentageFitValue,
          cachedCandidateFilters,
          { semantic: FEATURE_FLAGS.enableSemanticSearch && !!isSemanticEnabled },
        );
        setShowLoader(true);
        try {
          await dispatch(generateCandidate(payload)).unwrap();
          dispatch(resetCandidates());
          if (aboutFor) {
            navigateTo(`/qualified-candidates?jobId=${jobId}&fitPercent=${percentageFitValue}&about=${aboutFor}`);
          } else {
            navigateTo(`/qualified-candidates?jobId=${jobId}&fitPercent=${percentageFitValue}`);
          }
        } catch (error) {
          console.info('Error:', error);
        } finally {
          setShowLoader(false);
        }
      } else {
        if (endpoint === 'idealize-candidate') {
          for (const list of havesData) {
            if (list.title === "Won't-Haves") continue;
            if (!list.items || list.items.length === 0) {
              toast.error(`'${list.title}' should have at least 1 input.`, { closeButton: renderCustomCloseButton() });
              return false;
            }
          }
        }
        const payload: Record<string, any> = {
          approve_next: true,
          upcoming_step: nextStep,
        };
        if (endpoint === 'job-description-summary' && FEATURE_FLAGS.enableSemanticSearch) {
          payload.semantic = !!isSemanticEnabled;
        }
        setShowLoader(true);
        try {
          await dispatch(approvedJob({ payload, id: jobId })).unwrap();
          if (aboutFor) {
            navigateTo(`${approveRoute}?jobId=${jobId}&about=${aboutFor}`);
          } else {
            navigateTo(`${approveRoute}?jobId=${jobId}`);
          }
        } catch (error) {
          console.info('Error:', error);
        } finally {
          setShowLoader(false);
        }
      }
    }
  };

  const handleClose = async () => {
    if (jobAction === 'edit') {
      if (aboutFor) {
        navigateTo(`/job-description-summary?jobId=${jobId}&about=${aboutFor}`);
      } else {
        navigateTo(`/job-description-summary?jobId=${jobId}`);
      }
    } else {
      const payload = { approve_next: false, upcoming_step: nextStep };
      try {
        await dispatch(approvedJob({ payload, id: jobId })).unwrap();
        navigateTo('/dashboard');
      } catch (error) {
        console.info('Error:', error);
      }
    }
  };



  const transformJobData = (jobDetail: any) => {
    return [
      {
        title: 'Must-Haves',
        items: jobDetail?.must_have?.title || [],
        limit: 5,
        showLimit: true
      },
      {
        title: 'Should-Haves',
        items: jobDetail?.should_have?.title || [],
        limit: 7,
        showLimit: true
      },
      {
        title: 'Good-to-Haves',
        items: jobDetail?.good_to_have?.title || [],
        limit: 10,
        showLimit: true
      },
      {
        title: "Won't-Haves",
        items: jobDetail?.wont_have?.title || [],
        limit: 10,
        showLimit: true
      },
      {
        title: 'Could-Haves',
        items: jobDetail?.could_have?.title || [],
        limit: 150,
        showLimit: false
      },
    ];
  };

  const fetchJobDetails = useCallback(async (id: any, reloadRequired: any) => {
    try {
      setLoading(reloadRequired);
      const res = await dispatch(getJobDetail(id)).unwrap();
      setJobData(res?.data);
      setGeneratedDate(res?.data?.updated_at);
      setIdealizeJobData(transformJobData(res?.data));
      setLoading(false);
      localStorage.removeItem('isGenerate');
    } catch (error) {
      console.info('Error during fetching user details:', error);
      localStorage.removeItem('isGenerate');
    }
  }, [dispatch, setGeneratedDate]);

  const refreshJobData = (isRefresh: any = true) => {
    fetchJobDetails(jobId, isRefresh);
  };

  const fetchUserDetails = useCallback(async () => {
    try {
      const companyId = getValue('companyId');
      const payload = {
        companyId,
        loader: false
      };
      const res = await dispatch(getUserProfile(payload)).unwrap();
      storeValue('canCreateJob', res.data.job_left);
      if (res?.data?.tier_assigned) {
        // Cache tier data for faster loading on subsequent visits
        storeValue('tierData', JSON.stringify(res?.data?.tier_assigned));
        setAssignedTier(res?.data?.tier_assigned);
      }
    } catch (error) {
      console.info('Error during fetching user details:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (checkGenerate) {
      setDataGenerated(false);
    } else {
      setDataGenerated(true);
    }
  }, [checkGenerate]);

  useEffect(() => {
    // Only fetch user details once on mount
    fetchUserDetails();
    // fetchUserDetails is stable (only depends on dispatch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails(jobId, true);
    }
  }, [jobId, fetchJobDetails, endpoint]);

  useEffect(() => {
    setCachedCandidateFilters(null);
  }, [jobId]);

  useEffect(() => {
    if (endpoint !== 'candidate-selection') {
      setCachedCandidateFilters(null);
    }
  }, [endpoint]);

  useEffect(() => {
    if (endpoint !== 'job-description-summary' || !jobId) return;

    const resetActiveFilters = async () => {
      try {
        await dispatch(resetSearchFilters({ job: jobId })).unwrap();
      } catch (error) {
        console.info('Error resetting search filters:', error);
      } finally {
        clearActiveCandidateFilters(jobId);
      }
    };

    resetActiveFilters();
  }, [dispatch, endpoint, jobId]);

  // CandidateSelection syncs loadGraph -> candidateLoading; leaving that step unmounts the child
  // without resetting parent state, so "Generate Candidates" on the saved summary could stay disabled.
  useEffect(() => {
    if (endpoint !== 'candidate-selection') {
      setCandidateLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    setIsSemanticEnabled(
      FEATURE_FLAGS.enableSemanticSearch ? readSemanticPreference(jobId) : false,
    );
  }, [jobId]);

  useEffect(() => {
    if (!FEATURE_FLAGS.enableSemanticSearch) return;
    try {
      localStorage.setItem(getSemanticStorageKey(jobId), String(isSemanticEnabled));
    } catch {
      // ignore storage errors
    }
  }, [jobId, isSemanticEnabled]);

  const showSemanticToggle =
    FEATURE_FLAGS.enableSemanticSearch && endpoint === 'job-description-summary';

  return (
    <>
      <div className={`sign-in-header dashboard-header chat-header job-des-header ${!dataGenerated ? 'd-none' : ''}`}>
        <div className="container-fluid">
          <div className="chat-header-inner">
            <img
              src={logo}
              alt="logo"
              onClick={() => navigateTo('/dashboard')}
              role="button"
              aria-label="Go to dashboard"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigateTo('/dashboard'); } }}
              className="brand-logo"
            />
            <div className="welcome-wrap">
              <div className='dash-job-head'>
                <h4 className="dashboard-subhead text-uppercase">{pageHeading}</h4>
                {(((aboutFor === 'saved') || (aboutFor === 'duplicateSaved')) && (endpoint === 'job-description-summary')) && (
                  <p className='sub-desc-date'>
                    {generatedDate ? moment(generatedDate).format('MM/DD/YYYY') : ''}
                  </p>
                )}
              </div>
            </div>
            <div className={`chat-header-right ${showLoader ? 'p-none' : ''}`}>
              <Button
                variant="primary"
                className={`upgrade-header-btn ${!assignedTier || !shouldShowUpgradeButton(assignedTier) ? 'hidden' : ''}`}
                onClick={handleUpgrade}
              // Using CSS class for styling
              >
                <span className="d-none d-md-inline">Contact us to upgrade</span>
                <span className="d-md-none">Upgrade</span>
              </Button>
              <div className="job-header-action-group">
                <Button
                  disabled={(endpoint === 'candidate-selection' && percentageFitValue == null) || isLoading || candidateLoading}
                  variant="dark"
                  onClick={handleApprove}
                >
                  {endpoint !== 'candidate-selection' && (
                    <Image
                      src={likeWhite}
                      height={23}
                      width={23}
                      alt="arrow"
                      className="prefix-icon"
                    />
                  )}
                  <span className="text">{getButtonText()}</span>
                </Button>
                {showSemanticToggle && (
                  <div className="semantic-toggle-row">
                    <label htmlFor="semantic-search-toggle-desktop" className="semantic-toggle-label">
                      Semantic
                    </label>
                    <div className="mask-control semantic-toggle-control">
                      <input
                        id="semantic-search-toggle-desktop"
                        name="semanticSearch"
                        type="checkbox"
                        className="mask-toggle"
                        checked={isSemanticEnabled}
                        onChange={(e) => setIsSemanticEnabled(e.target.checked)}
                        disabled={isLoading || showLoader}
                      />
                      <label
                        htmlFor="semantic-search-toggle-desktop"
                        className="mask-switch"
                        aria-label="toggle semantic search"
                      ></label>
                    </div>
                  </div>
                )}
              </div>
              <Button className="btn-cancel btn-cancel-filled" onClick={handleClose}>
                {jobAction === 'edit' ? 'Cancel' : 'Close'}
              </Button>
              <DropdownMenu assignedTier={assignedTier} />
            </div>
          </div>
        </div>
      </div>

      <section
        className={`preview-container ${!dataGenerated ? 'd-none' : ''} ${endpoint === 'job-description-summary' ? 'preview-detail-container' : 'preview-container-chat'}`}>
        <ThemeChange />
        <Description
          jobId={jobId}
          step={endpoint}
          loading={loading}
          jobData={jobData}
          jobAction={jobAction}
          aboutFor={aboutFor}
          setShowLoader={setShowLoader}
          idealizeJobData={idealizeJobData}
          setPercentageFitValue={setPercentageFitValue}
          hideChatWindow={hideChatWindow}
          setHideChatWindow={setHideChatWindow}
          refreshJobData={refreshJobData}
          setHavesData={setHavesData}
          handleClose={handleClose}
          setCandidateLoading={setCandidateLoading}
          assignedTier={assignedTier}
          onCachedFiltersChange={setCachedCandidateFilters}
        />

        <div className="mobile-footer">
          {((endpoint === 'job-description') || (endpoint === 'idealize-candidate')) && (
            <Image
              src={aiChat}
              height={60}
              width={60}
              alt="Chat bot"
              className="btn-round"
              onClick={() => setHideChatWindow(false)}
              role="button"
              aria-label="Open chat"
              tabIndex={0}
              onKeyDown={(e: any) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHideChatWindow(false); } }}
            />
          )}
          <div className="mobile-footer-action-group">
            <Button
              variant="dark"
              className='generate-candidate-btn'
              disabled={(endpoint === 'candidate-selection' && percentageFitValue == null) || isLoading || candidateLoading}
              onClick={handleApprove}
            >
              {endpoint !== 'candidate-selection' && (
                <Image
                  src={likeWhite}
                  height={23}
                  width={23}
                  alt="arrow"
                  className="prefix-icon"
                />
              )}
              <span className="text">{getButtonText()}</span>
            </Button>
            {showSemanticToggle && (
              <div className="semantic-toggle-row">
                <label htmlFor="semantic-search-toggle-mobile" className="semantic-toggle-label">
                  Semantic
                </label>
                <div className="mask-control semantic-toggle-control">
                  <input
                    id="semantic-search-toggle-mobile"
                    name="semanticSearch"
                    type="checkbox"
                    className="mask-toggle"
                    checked={isSemanticEnabled}
                    onChange={(e) => setIsSemanticEnabled(e.target.checked)}
                    disabled={isLoading || showLoader}
                  />
                  <label
                    htmlFor="semantic-search-toggle-mobile"
                    className="mask-switch"
                    aria-label="toggle semantic search"
                  ></label>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className={`chat-section chat-loading-page chat-dashboard-loading-sec ${!dataGenerated ? '' : 'chat-section-done'}`}>
        <div className="video-container">
          <video
            className="footer-video"
            controlsList="nodownload"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          >
            <source src={EXTERNAL_URLS.VIDEOS.LANDING_PAGE} type="video/mp4" />
          </video>
          <div className="video-overlay"></div>
        </div>
        <div className="container">
          <div className="chat-dashboard-loading done-status">
            <div className="done-wrap">
              <p className="chat-dashboard-loading-des">Done</p>
              <span className="green-tick"><img src={doneTick} alt="tick" /></span>
            </div>
          </div>
        </div>
        <img src={chatBottonIcon} alt="brand" className="chat-bottom-img" />
      </section>
    </>
  );
};

export default JobDescriptionPage;
